"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useOnboardingModal } from "@/contexts/onboarding/OnboardingModalContext";
import { GailProductRoles } from "@/enums/user";
import { useOrg } from "@/hooks/useOrg";
import {
  ConfigurationStatus,
  detectExistingConfiguration,
} from "@/utils/onboarding/configDetection";

const EMPTY_CONFIGURATION_STATUS: ConfigurationStatus = {
  hasInboundScript: false,
  hasOutboundScript: false,
  hasAssistant: false,
  hasDirectory: false,
  hasBusinessHours: false,
  hasCallConcurrency: false,
  hasFaqs: false,
  hasNotifications: false,
  hasCompanyInfo: false,
  hasConnectedCalendar: false,
  hasCustomVoice: false,
  hasConnectedIntegration: false,
  hasApprovedSmsCampaign: false,
  hasConversations: false,
};

/**
 * Detects existing org configuration, re-detects when the onboarding modal
 * closes, and supplements the result with live context data so the UI reacts
 * immediately to saves (business hours, company info) without a full
 * re-detection round-trip.
 */
export function useConfigurationStatus(): ConfigurationStatus | null {
  const { org, agentProfile, productRolesState } = useOrg();
  const { isOpen: isModalOpen } = useOnboardingModal();

  const hasAgentAccess = productRolesState.roles?.includes(GailProductRoles.GailAgentUser) ?? false;

  const [configStatus, setConfigStatus] = useState<ConfigurationStatus | null>(null);
  // Keyed by org id rather than a plain "already ran" flag so a soft org
  // switch re-detects instead of serving the previous org's results.
  const detectedOrgIdRef = useRef<string | null>(null);

  // Initial detection -- only for users with agent access (onboarding is agent-specific).
  // Runs regardless of onboarding status: the dashboard's recommended actions
  // panel needs accurate config data even after onboarding is completed.
  useEffect(() => {
    const orgId = org?.id;
    if (!orgId || !hasAgentAccess || detectedOrgIdRef.current === orgId) return;

    setConfigStatus(null);

    let cancelled = false;
    detectExistingConfiguration(org)
      .then((status) => {
        if (cancelled) return;
        // Stamp the org only after a result is applied. If the effect re-runs
        // before the promise settles (useOrg swapping the cached org for the
        // fetched one with the same id, or a Strict Mode replay), the cleanup
        // cancels this handler and the re-run must be allowed to attach again.
        // Stamping in setup made the re-run bail and left configStatus null,
        // so the dashboard sat on its skeleton forever.
        detectedOrgIdRef.current = orgId;
        setConfigStatus(status);
      })
      .catch(() => {
        if (cancelled) return;
        detectedOrgIdRef.current = orgId;
        setConfigStatus(EMPTY_CONFIGURATION_STATUS);
      });
    return () => {
      cancelled = true;
    };
  }, [org, hasAgentAccess]);

  // Re-detect when the onboarding modal closes (user may have completed steps)
  const prevModalOpenRef = useRef(isModalOpen);
  useEffect(() => {
    const shouldRedetect = prevModalOpenRef.current && !isModalOpen && org && hasAgentAccess;
    prevModalOpenRef.current = isModalOpen;
    if (!shouldRedetect) return;

    let cancelled = false;
    detectExistingConfiguration(org).then((status) => {
      if (!cancelled) setConfigStatus(status);
    });
    return () => {
      cancelled = true;
    };
  }, [isModalOpen, org, hasAgentAccess]);

  // Supplement with live context data so changes are reflected immediately
  return useMemo<ConfigurationStatus | null>(() => {
    if (!configStatus) return null;

    const liveHasBusinessHours = Boolean(
      agentProfile?.operationSchedule?.businessSchedule?.some(
        (day) => day.hours && day.hours.length > 0
      )
    );
    const liveHasCompanyInfo = Boolean(org?.name?.trim() && org?.address?.addressLine1);

    return {
      ...configStatus,
      hasBusinessHours: configStatus.hasBusinessHours || liveHasBusinessHours,
      hasCompanyInfo: configStatus.hasCompanyInfo || liveHasCompanyInfo,
    };
  }, [configStatus, agentProfile, org]);
}
